import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import English translation files
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enNavigation from './locales/en/navigation.json';
import enDashboard from './locales/en/dashboard.json';
import enProperties from './locales/en/properties.json';
import enAssessments from './locales/en/assessments.json';
import enAssessmentsPerform from './locales/en/assessments-perform.json';
import enUsers from './locales/en/users.json';
import enUsersCreate from './locales/en/users-create.json';
import enTemplates from './locales/en/templates.json';
import enForms from './locales/en/forms.json';
import enMessages from './locales/en/messages.json';
import enMissedStandards from './locales/en/missed-standards.json';
import enGuidelines from './locales/en/guidelines.json';

// Import Arabic translation files
import arCommon from './locales/ar/common.json';
import arAuth from './locales/ar/auth.json';
import arNavigation from './locales/ar/navigation.json';
import arDashboard from './locales/ar/dashboard.json';
import arProperties from './locales/ar/properties.json';
import arAssessments from './locales/ar/assessments.json';
import arAssessmentsPerform from './locales/ar/assessments-perform.json';
import arUsers from './locales/ar/users.json';
import arUsersCreate from './locales/ar/users-create.json';
import arTemplates from './locales/ar/templates.json';
import arForms from './locales/ar/forms.json';
import arMessages from './locales/ar/messages.json';
import arMissedStandards from './locales/ar/missed-standards.json';
import arGuidelines from './locales/ar/guidelines.json';

const resources = {
  en: {
    translation: {
      // Common translations at root level
      welcome: enCommon.welcome,
      loading: enCommon.loading,
      save: enCommon.save,
      cancel: enCommon.cancel,
      delete: enCommon.delete,
      edit: enCommon.edit,
      add: enCommon.add,
      search: enCommon.search,
      filter: enCommon.filter,
      submit: enCommon.submit,
      submitting: 'Submitting...',
      confirm: enCommon.confirm,
      yes: enCommon.yes,
      no: enCommon.no,
      back: enCommon.back,
      next: enCommon.next,
      previous: enCommon.previous,
      close: enCommon.close,
      open: enCommon.open,
      view: enCommon.view,
      download: enCommon.download,
      upload: enCommon.upload,
      select: enCommon.select,
      all: enCommon.all,
      none: enCommon.none,
      actions: enCommon.actions,
      status: enCommon.status,
      date: enCommon.date,
      time: enCommon.time,
      name: enCommon.name,
      description: enCommon.description,
      type: enCommon.type,
      total: enCommon.total,
      count: enCommon.count,
      language: enCommon.language,
      touchpoints: enCommon.touchpoints,
      photo: enCommon.photo,
      audio: enCommon.audio,
      complete: enCommon.complete,
      supportTeam: enCommon.supportTeam,
      pagination: enCommon.pagination,
      of: 'of',
      success: 'Success',
      error: 'Error',
      answerUpdated: 'Answer updated',
      answerUpdateFailed: 'Failed to update answer',
      mediaUploaded: 'Media uploaded',
      mediaUploadFailed: 'Failed to upload media',
      assessmentSubmitted: 'Assessment submitted',
      // Common namespace for compatibility
      common: enCommon,
      // Namespaced translations
      auth: enAuth,
      navigation: enNavigation,
      dashboard: enDashboard,
      properties: enProperties,
      assessments: { ...enAssessments, ...enAssessmentsPerform },
      users: { ...enUsers, ...enUsersCreate },
      templates: enTemplates,
      forms: enForms,
      messages: enMessages,
      missedStandards: enMissedStandards,
      guidelines: enGuidelines,
    },
  },
  ar: {
    translation: {
      // Common translations at root level
      welcome: arCommon.welcome,
      loading: arCommon.loading,
      save: arCommon.save,
      cancel: arCommon.cancel,
      delete: arCommon.delete,
      edit: arCommon.edit,
      add: arCommon.add,
      search: arCommon.search,
      filter: arCommon.filter,
      submit: arCommon.submit,
      submitting: 'جاري الإرسال...',
      confirm: arCommon.confirm,
      yes: arCommon.yes,
      no: arCommon.no,
      back: arCommon.back,
      next: arCommon.next,
      previous: arCommon.previous,
      close: arCommon.close,
      open: arCommon.open,
      view: arCommon.view,
      download: arCommon.download,
      upload: arCommon.upload,
      select: arCommon.select,
      all: arCommon.all,
      none: arCommon.none,
      actions: arCommon.actions,
      status: arCommon.status,
      date: arCommon.date,
      time: arCommon.time,
      name: arCommon.name,
      description: arCommon.description,
      type: arCommon.type,
      total: arCommon.total,
      count: arCommon.count,
      language: arCommon.language,
      photo: arCommon.photo,
      audio: arCommon.audio,
      complete: arCommon.complete,
      supportTeam: arCommon.supportTeam,
      pagination: arCommon.pagination,
      of: 'من',
      success: 'نجاح',
      error: 'خطأ',
      answerUpdated: 'تم تحديث الإجابة',
      answerUpdateFailed: 'فشل تحديث الإجابة',
      mediaUploaded: 'تم رفع الوسائط',
      mediaUploadFailed: 'فشل رفع الوسائط',
      assessmentSubmitted: 'تم إرسال التقييم',
      // Common namespace for compatibility
      common: arCommon,
      // Namespaced translations
      auth: arAuth,
      navigation: arNavigation,
      dashboard: arDashboard,
      properties: arProperties,
      assessments: { ...arAssessments, ...arAssessmentsPerform },
      users: { ...arUsers, ...arUsersCreate },
      templates: arTemplates,
      forms: arForms,
      messages: arMessages,
      missedStandards: arMissedStandards,
      guidelines: arGuidelines,
    },
  },
};

// Get initial language from localStorage or default to 'en'
const getInitialLanguage = (): string => {
  try {
    const saved = localStorage.getItem('i18nextLng');
    if (saved && (saved === 'en' || saved === 'ar')) {
      return saved;
    }
  } catch (error) {
    console.warn('Failed to read language from localStorage:', error);
  }
  return 'en';
};

const initialLanguage = getInitialLanguage();

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: initialLanguage,
    fallbackLng: 'en',
    debug: false,

    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag', 'path', 'subdomain'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
  });

// Function to get RTL languages
export const isRTL = (lng: string): boolean => {
  return lng === 'ar';
};

// Function to update document direction
export const updateDocumentDirection = (lng: string): void => {
  const dir = isRTL(lng) ? 'rtl' : 'ltr';
  document.documentElement.dir = dir;
  document.documentElement.lang = lng;
};

// Listen to language changes and update document direction
i18n.on('languageChanged', updateDocumentDirection);

// Initialize document direction on app start
updateDocumentDirection(initialLanguage);

// Make i18n available globally for non-React contexts (like API hooks)
declare global {
  interface Window {
    i18n: typeof i18n;
  }
}
if (typeof window !== 'undefined') {
  window.i18n = i18n;
}

export default i18n;
